import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type DependencyList,
  type ReactNode,
} from 'react';
import type { PageHeaderConfig, RouteTitle } from './types';

interface PageHeaderRegistration {
  setPageHeader: (config: PageHeaderConfig | null) => void;
}

const PageHeaderContext = createContext<PageHeaderRegistration | null>(null);

export function PageHeaderProvider({
  fallback,
  children,
}: {
  fallback: RouteTitle | PageHeaderConfig;
  children: (config: PageHeaderConfig) => ReactNode;
}) {
  const [registered, setRegistered] = useState<PageHeaderConfig | null>(null);
  const setPageHeader = useCallback((config: PageHeaderConfig | null) => setRegistered(config), []);
  const registration = useMemo(() => ({ setPageHeader }), [setPageHeader]);
  const fallbackConfig = useMemo<PageHeaderConfig>(
    () => ('sub' in fallback ? { title: fallback.title, subtitle: fallback.sub } : fallback),
    [fallback],
  );

  return (
    <PageHeaderContext.Provider value={registration}>
      {children(registered ?? fallbackConfig)}
    </PageHeaderContext.Provider>
  );
}

/**
 * Registers page identity and actions with the nearest AppShell.
 *
 * Callers provide the values that should refresh the registration, keeping
 * dynamic record headers concise without duplicating effect boilerplate.
 */
export function usePageHeader(config: PageHeaderConfig, dependencies: DependencyList) {
  const registration = useContext(PageHeaderContext);
  useLayoutEffect(() => {
    if (!registration) return;
    registration.setPageHeader(config);
    return () => registration.setPageHeader(null);
    // The caller intentionally controls when callbacks and record state refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registration, ...dependencies]);
}
